import { useMemo } from "react";
import type { VersionTick } from "@norkron/web-review-workbench";
import { Repeat2, SlidersHorizontal, X } from "../../compose/Icon";

/**
 * `<CompareTray>` — the W4 "pick any two versions" tray (design §2.7). A small
 * floating panel that shows the current `A: v{seqA} ↔ B: v{seqB}` selection, a
 * per-version row of Set-A / Set-B buttons, a Swap, and a Diff action. Purely
 * presentational — `useVersionCompare` owns the state + the fetch; this component
 * only renders the pins + emits the pick/swap/diff/close intents. Scope-agnostic:
 * the SAME tray drives both the whole-resource compare (ticks = resource version
 * stream) and the section-scoped compare (ticks = section version stream).
 */
export interface CompareTrayProps {
  readonly title: string;
  /** Version ticks to pick from (any order; rendered newest-first). */
  readonly ticks: readonly VersionTick[];
  /** The version id pinned as A. */
  readonly selectedA?: string;
  /** The version id pinned as B. */
  readonly selectedB?: string;
  readonly onSetA: (versionId: string) => void;
  readonly onSetB: (versionId: string) => void;
  readonly onSwap: () => void;
  readonly onDiff: () => void;
  readonly onClose: () => void;
  readonly testId?: string;
}

function seqOf(
  ticks: readonly VersionTick[],
  versionId: string | undefined,
): number | undefined {
  if (versionId === undefined) return undefined;
  return ticks.find((t) => t.versionId === versionId)?.seq;
}

export function CompareTray({
  title,
  ticks,
  selectedA,
  selectedB,
  onSetA,
  onSetB,
  onSwap,
  onDiff,
  onClose,
  testId,
}: CompareTrayProps) {
  // Newest at the top — matches the vertical `<TimelineBar>` order.
  const ordered = useMemo(
    () => [...ticks].sort((a, b) => b.seq - a.seq),
    [ticks],
  );
  const aSeq = seqOf(ticks, selectedA);
  const bSeq = seqOf(ticks, selectedB);
  const canDiff =
    selectedA !== undefined &&
    selectedB !== undefined &&
    selectedA !== selectedB;

  return (
    <div
      className="wb-compare-tray"
      role="dialog"
      aria-label={title}
      data-testid={testId ?? "compare-tray"}
    >
      <div className="wb-compare-tray__head">
        <span className="wb-compare-tray__title">
          <SlidersHorizontal size={13} strokeWidth={1.5} aria-hidden="true" />
          {title}
        </span>
        <button
          type="button"
          className="wb-compare-tray__close"
          data-testid="compare-tray-close"
          aria-label="Close compare"
          onClick={onClose}
        >
          <X size={13} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>

      <div className="wb-compare-tray__selection" data-testid="compare-tray-selection">
        A: {aSeq !== undefined ? `v${aSeq}` : "—"}
        <span className="wb-compare-tray__arrow" aria-hidden="true">
          {" ↔ "}
        </span>
        B: {bSeq !== undefined ? `v${bSeq}` : "—"}
      </div>

      <ul className="wb-compare-tray__ticks" aria-label="Versions">
        {ordered.map((t) => (
          <li key={t.versionId} className="wb-compare-tray__row">
            <span className="wb-compare-tray__label">
              {t.label}
              {t.isHead ? " · head" : ""}
            </span>
            <button
              type="button"
              className="wb-compare-tray__pick"
              data-testid={`compare-set-a-${t.seq}`}
              data-active={t.versionId === selectedA ? "true" : undefined}
              aria-pressed={t.versionId === selectedA}
              title={`Pin v${t.seq} as A`}
              onClick={() => onSetA(t.versionId)}
            >
              A
            </button>
            <button
              type="button"
              className="wb-compare-tray__pick"
              data-testid={`compare-set-b-${t.seq}`}
              data-active={t.versionId === selectedB ? "true" : undefined}
              aria-pressed={t.versionId === selectedB}
              title={`Pin v${t.seq} as B`}
              onClick={() => onSetB(t.versionId)}
            >
              B
            </button>
          </li>
        ))}
      </ul>

      <div className="wb-compare-tray__actions">
        <button
          type="button"
          className="wb-compare-tray__swap"
          data-testid="compare-swap"
          onClick={onSwap}
        >
          <Repeat2 size={13} strokeWidth={1.5} aria-hidden="true" />
          Swap
        </button>
        <button
          type="button"
          className="wb-compare-tray__diff"
          data-testid="compare-diff"
          disabled={!canDiff}
          onClick={onDiff}
        >
          Diff
        </button>
      </div>
    </div>
  );
}
